// Super Admin → Features (iter166)
// Fully DYNAMIC — driven by /api/super/features/registry. Adding a new
// feature requires only one entry in `TENANT_FEATURE_REGISTRY` (backend).
// This page renders it automatically — grouped, searchable, per-tenant.
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import {
  ShieldCheck, Building2, Search, ChefHat, DollarSign, Package, BarChart3, Boxes,
  Sparkles, Users, FileText, Settings, MessageCircle, Megaphone, ClipboardList,
  Hammer, FlaskConical, Bell, Plug, Lock, Heart, Pin, PinOff,
} from 'lucide-react';
import SuperAdminBUEntitlements from './SuperAdminBUEntitlements';

// iter179 — Features eligible to be used as a tenant's login landing page.
// Mirrors backend FEATURE_TO_ADMIN_PATH in /app/backend/routes/welcome.py.
// Utility flags (audit_logs, exports, notifications, integrations) are not
// landing-eligible because they have no standalone page.
const LANDING_ELIGIBLE = new Set([
  'dashboard', 'financial_dashboard', 'pnl_live', 'cost_insights', 'reports',
  'crm', 'customer_feedback', 'custom_feedback_forms', 'reservations_module',
  'checklist', 'maintenance',
  'inventory_module', 'recipe_module', 'production_planner',
  'petty_cash', 'communication', 'marketing',
  'user_management', 'outlet_management',
]);

// iter180 — Parent-feature keys that gate the top-nav groups. UI surfaces
// them with a distinct visual treatment (cascade-off confirmation modal).
const PARENT_FEATURES = new Set([
  'overview_home', 'financials_home', 'operations_home', 'marketing_home',
  'customers_home', 'communication_home', 'organization_home',
]);

const ICONS = {
  // Operations
  dashboard: BarChart3, checklist: ClipboardList, kitchen_closing: ChefHat,
  bar_closing: FlaskConical, maintenance: Hammer,
  // Finance
  pnl_live: DollarSign, pnl_finalize_compare: DollarSign, pnl_statements: DollarSign,
  review_sheet: FileText, petty_cash: DollarSign,
  // CX
  customer_feedback: MessageCircle, custom_feedback_forms: Sparkles, crm: Heart,
  // Marketing
  marketing: Megaphone, campaigns: Megaphone, communication: MessageCircle,
  // Inventory
  inventory_module: Boxes, recipe_module: ChefHat, pricing_module: DollarSign,
  production_planner: Package, cost_insights: BarChart3,
  // Administration
  user_management: Users, outlet_management: Building2, approvals: Lock, audit_logs: FileText,
  // Reporting
  reports: FileText, exports: FileText,
  // Integrations
  notifications: Bell, integrations: Plug,
};

export default function SuperAdminFeatures() {
  const { API, getHeaders } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [features, setFeatures] = useState({});      // {tenant_id: {flag: bool}}
  const [registry, setRegistry] = useState([]);      // [{key, label, group, description, default}]
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tenantSearch, setTenantSearch] = useState('');
  const [activeGroup, setActiveGroup] = useState('All');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [busy, setBusy] = useState({});
  // iter179 — Tenant landing-module override. {tenant_id: feature_key | null}
  const [landingByTenant, setLandingByTenant] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [reg, tenantsRes] = await Promise.all([
          axios.get(`${API}/super/features/registry`, { headers: getHeaders() }),
          axios.get(`${API}/super/tenants`, { headers: getHeaders() }),
        ]);
        setRegistry(reg.data.features || []);
        setGroups(reg.data.groups || []);
        const list = tenantsRes.data.items || tenantsRes.data || [];
        setTenants(list);
        if (list.length) setSelectedTenant(list[0].tenant_id);
        const results = await Promise.all(
          list.map((t) =>
            axios.get(`${API}/super/tenants/${t.tenant_id}/features`, { headers: getHeaders() })
              .then((r) => [t.tenant_id, r.data])
              .catch(() => [t.tenant_id, null]),
          ),
        );
        const acc = {};
        results.forEach(([tid, flags]) => { if (flags) acc[tid] = flags; });
        setFeatures(acc);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load registry');
      } finally {
        setLoading(false);
      }
    })();
  }, [API, getHeaders]);

  const toggle = async (tenantId, featureKey, value) => {
    // iter180 — Parent-feature toggle: warn that this cascades off all children.
    if (PARENT_FEATURES.has(featureKey) && value === false) {
      const parentChildren = {
        overview_home: ['Dashboard'],
        financials_home: ['5-Minute Dashboard', 'P&L Live', 'Cost Insights', 'Petty Cash', 'Review Sheet', 'P&L Statements', 'P&L Finalize', 'Reports'],
        operations_home: ['Compliance', 'Checklist', 'Maintenance', 'Inventory', 'Recipes', 'Production Planner', 'Approvals'],
        marketing_home: ['Marketing', 'Campaigns'],
        customers_home: ['CRM', 'Customer Experience', 'Customer Pulse Index', 'Reservations'],
        communication_home: ['Communication'],
        organization_home: ['User Management', 'Outlet Management', 'Audit Logs'],
      };
      const list = parentChildren[featureKey] || [];
      const groupLabel = registry.find((f) => f.key === featureKey)?.label || featureKey;
      const confirmed = window.confirm(
        `Disable ${groupLabel}?\n\nThis will hide the entire navbar group and disable ${list.length} child features for this tenant:\n\n• ${list.join('\n• ')}\n\nContinue?`,
      );
      if (!confirmed) return;
    }
    const bKey = `${tenantId}:${featureKey}`;
    setBusy((b) => ({ ...b, [bKey]: true }));
    // Optimistic
    setFeatures((prev) => ({
      ...prev,
      [tenantId]: { ...(prev[tenantId] || {}), [featureKey]: value },
    }));
    try {
      const res = await axios.put(
        `${API}/super/tenants/${tenantId}/features`,
        { [featureKey]: value },
        { headers: getHeaders() },
      );
      setFeatures((prev) => ({ ...prev, [tenantId]: res.data }));
      const label = registry.find((f) => f.key === featureKey)?.label || featureKey;
      toast.success(`${label} ${value ? 'enabled' : 'disabled'}`);
    } catch (e) {
      setFeatures((prev) => ({
        ...prev,
        [tenantId]: { ...(prev[tenantId] || {}), [featureKey]: !value },
      }));
      toast.error(e?.response?.data?.detail || 'Failed to update');
    } finally {
      setBusy((b) => { const { [bKey]: _, ...rest } = b; return rest; });
    }
  };

  // iter179 — Load + update tenant's chosen landing module.
  useEffect(() => {
    if (!selectedTenant) return;
    axios
      .get(`${API}/super/tenants/${selectedTenant}/landing-module`, { headers: getHeaders() })
      .then((r) => setLandingByTenant((m) => ({ ...m, [selectedTenant]: r.data.landing_module || null })))
      .catch(() => setLandingByTenant((m) => ({ ...m, [selectedTenant]: null })));
  }, [selectedTenant, API, getHeaders]);

  const setLanding = async (tenantId, featureKey) => {
    const isCurrent = landingByTenant[tenantId] === featureKey;
    const newValue = isCurrent ? null : featureKey;
    const prev = landingByTenant[tenantId];
    setLandingByTenant((m) => ({ ...m, [tenantId]: newValue }));
    try {
      await axios.put(
        `${API}/super/tenants/${tenantId}/landing-module`,
        { landing_module: newValue || '' },
        { headers: getHeaders() },
      );
      const label = registry.find((f) => f.key === featureKey)?.label || featureKey;
      toast.success(newValue ? `${label} set as login landing page` : `${label} unpinned from landing`);
    } catch (e) {
      setLandingByTenant((m) => ({ ...m, [tenantId]: prev }));
      toast.error(e?.response?.data?.detail || 'Failed to set landing module');
    }
  };

  const visibleFeatures = useMemo(() => {
    const term = search.trim().toLowerCase();
    return registry.filter((f) => {
      if (activeGroup !== 'All' && f.group !== activeGroup) return false;
      if (!term) return true;
      return (
        f.label.toLowerCase().includes(term) ||
        f.key.toLowerCase().includes(term) ||
        (f.description || '').toLowerCase().includes(term)
      );
    });
  }, [registry, activeGroup, search]);

  const filteredTenants = useMemo(() => {
    const t = tenantSearch.trim().toLowerCase();
    return tenants.filter((x) =>
      !t || (x.name || '').toLowerCase().includes(t) || (x.tenant_id || '').toLowerCase().includes(t),
    );
  }, [tenants, tenantSearch]);

  const featuresByGroup = useMemo(() => {
    const acc = {};
    visibleFeatures.forEach((f) => { (acc[f.group] = acc[f.group] || []).push(f); });
    return acc;
  }, [visibleFeatures]);

  const currentFlags = (selectedTenant && features[selectedTenant]) || {};

  return (
    <div data-testid="super-features-page">
      <div className="mb-5">
        <h2 className="text-xl sm:text-2xl tracking-tight font-semibold text-white flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-emerald-400" /> Feature Control Panel
        </h2>
        <p className="text-sm text-slate-300 mt-1">
          Enable or disable platform modules per tenant. Disabled features are hidden from the UI and rejected at the API layer.
        </p>
        <p className="text-[11.5px] text-slate-400 mt-0.5">
          {registry.length} features across {groups.length} groups · {tenants.length} tenant{tenants.length === 1 ? '' : 's'}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-6 h-6 border-2 border-emerald-400 border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="grid lg:grid-cols-[280px_1fr] gap-4">
          {/* Tenant picker */}
          <Card className="border-[#E2E8DF] shadow-sm h-fit" data-testid="features-tenant-picker">
            <CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-[0.10em] text-[#5C6B64] font-semibold mb-2">Tenant</div>
              <div className="relative mb-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C6B64]" />
                <Input value={tenantSearch} onChange={(e) => setTenantSearch(e.target.value)}
                       placeholder="Search…" className="pl-8 h-8 text-[12px]" data-testid="features-tenant-search" />
              </div>
              <div className="max-h-[480px] overflow-y-auto space-y-1">
                {filteredTenants.length === 0 && (
                  <div className="text-center text-[12px] text-[#5C6B64] py-6">
                    <Building2 className="w-6 h-6 text-[#5C6B64]/30 mx-auto mb-1" />
                    No tenants
                  </div>
                )}
                {filteredTenants.map((t) => {
                  const sel = selectedTenant === t.tenant_id;
                  const flags = features[t.tenant_id] || {};
                  const enabledCount = registry.filter((f) => flags[f.key] !== false).length;
                  return (
                    <button key={t.tenant_id} onClick={() => setSelectedTenant(t.tenant_id)}
                      data-testid={`tenant-pick-${t.tenant_id}`}
                      className={`w-full text-left px-2.5 py-2 rounded-md text-[12.5px] transition-all border ${
                        sel
                          ? 'bg-[color:var(--color-primary)] text-white border-[color:var(--color-primary)]'
                          : 'bg-white text-[#1A2421] border-[#E2E8DF] hover:border-[color:var(--color-primary)]'
                      }`}>
                      <div className="font-semibold truncate">{t.name || t.tenant_id}</div>
                      <div className={`text-[10px] font-mono mt-0.5 ${sel ? 'text-emerald-200' : 'text-[#5C6B64]'}`}>
                        {t.tenant_id} · {enabledCount}/{registry.length} active
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Features list */}
          <div className="space-y-3">
            {selectedTenant && (
              <Card className="border-[#E2E8DF] bg-[#FAFBF8] shadow-sm" data-testid="landing-status-card">
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-md bg-[color:var(--color-primary)]/8 text-[color:var(--color-primary)] flex items-center justify-center">
                    <Pin className="w-4 h-4" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6B64] font-semibold">
                      Login Landing Page
                    </div>
                    <div className="text-[13px] text-[#1A2421] font-medium">
                      {landingByTenant[selectedTenant]
                        ? <>Users land on <span className="text-[color:var(--color-primary)] font-semibold">{registry.find((f) => f.key === landingByTenant[selectedTenant])?.label || landingByTenant[selectedTenant]}</span> after login.</>
                        : <>No explicit landing set — using automatic priority chain.</>}
                    </div>
                  </div>
                  <span className="text-[10px] text-[#9CA3A0] hidden sm:inline">
                    Pin any enabled feature below to set it as the landing page.
                  </span>
                </CardContent>
              </Card>
            )}
            {selectedTenant && (
              <SuperAdminBUEntitlements
                tenantId={selectedTenant}
                tenantName={tenants.find((t) => t.tenant_id === selectedTenant)?.name || selectedTenant}
              />
            )}
            <Card className="border-[#E2E8DF] shadow-sm">
              <CardContent className="p-3 flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6B64]" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)}
                         placeholder="Search features…" className="pl-9 h-9" data-testid="features-search" />
                </div>
                <Select value={activeGroup} onValueChange={setActiveGroup}>
                  <SelectTrigger className="h-9 w-[180px] text-[12.5px]" data-testid="features-group-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All groups</SelectItem>
                    {groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {!selectedTenant ? (
              <Card className="border-[#E2E8DF] border-dashed">
                <CardContent className="p-12 text-center text-[#5C6B64]">
                  <Building2 className="w-8 h-8 text-[#5C6B64]/30 mx-auto mb-2" />
                  Select a tenant on the left to manage its features.
                </CardContent>
              </Card>
            ) : Object.keys(featuresByGroup).length === 0 ? (
              <Card className="border-[#E2E8DF] border-dashed">
                <CardContent className="p-12 text-center text-[#5C6B64]">No features match your search.</CardContent>
              </Card>
            ) : (
              groups.filter((g) => featuresByGroup[g]).map((g) => (
                <Card key={g} className="border-[#E2E8DF] shadow-sm" data-testid={`feature-group-${g}`}>
                  <CardContent className="p-0">
                    <div className="px-4 py-2 border-b border-[#E2E8DF] bg-[#F7F9F7]">
                      <span className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--color-primary)] font-bold">{g}</span>
                      <span className="text-[10px] text-[#5C6B64] ml-2">{featuresByGroup[g].length} feature{featuresByGroup[g].length === 1 ? '' : 's'}</span>
                    </div>
                    <ul className="divide-y divide-[#E2E8DF]/60">
                      {featuresByGroup[g].map((f) => {
                        const Icon = ICONS[f.key] || Settings;
                        const enabled = currentFlags[f.key] !== undefined ? currentFlags[f.key] : f.default;
                        const bKey = `${selectedTenant}:${f.key}`;
                        const isParent = PARENT_FEATURES.has(f.key);
                        return (
                          <li key={f.key}
                              className={`px-4 py-3 flex items-center gap-3 hover:bg-[#F9F9F8] ${
                                isParent ? 'bg-[#FAFBF8] border-l-4 border-l-[var(--color-primary)]' : ''
                              }`}
                              data-testid={`feature-row-${f.key}`}>
                            <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
                              enabled ? (isParent ? 'bg-[color:var(--color-primary)] text-white' : 'bg-[#EEF4EF] text-[color:var(--color-primary)]') : 'bg-[#F0F2EE] text-[#9CA3A0]'
                            }`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold text-[#1A2421] text-[13.5px] truncate flex items-center">
                                {f.label}
                                {isParent && (
                                  <span className="ml-2 text-[9px] uppercase tracking-wider text-white bg-[color:var(--color-primary)] px-1.5 py-0.5 rounded font-bold">
                                    parent
                                  </span>
                                )}
                                {f.default === false && !isParent && (
                                  <span className="ml-2 text-[9px] uppercase tracking-wider text-[#D89B5B] bg-[#FFF6E5] px-1.5 py-0.5 rounded">
                                    opt-in
                                  </span>
                                )}
                              </div>
                              <div className="text-[11.5px] text-[#5C6B64] truncate">{f.description}</div>
                              <div className="text-[10px] text-[#9CA3A0] font-mono mt-0.5">{f.key}</div>
                            </div>
                            <Switch
                              checked={enabled}
                              disabled={!!busy[bKey]}
                              onCheckedChange={(v) => toggle(selectedTenant, f.key, v)}
                              data-testid={`toggle-${selectedTenant}-${f.key}`}
                            />
                            {LANDING_ELIGIBLE.has(f.key) && (
                              <button
                                type="button"
                                onClick={() => setLanding(selectedTenant, f.key)}
                                disabled={!enabled}
                                title={
                                  !enabled
                                    ? 'Enable this feature first'
                                    : landingByTenant[selectedTenant] === f.key
                                    ? 'Unpin as login landing page'
                                    : 'Use as login landing page'
                                }
                                className={`ml-1 w-7 h-7 rounded-md flex items-center justify-center transition-colors ${
                                  landingByTenant[selectedTenant] === f.key
                                    ? 'bg-[color:var(--color-primary)] text-white hover:bg-[#0B2A22]'
                                    : 'bg-transparent text-[#9CA3A0] hover:bg-[#F0F2EE] hover:text-[color:var(--color-primary)]'
                                } ${!enabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                                data-testid={`landing-pin-${selectedTenant}-${f.key}`}
                              >
                                {landingByTenant[selectedTenant] === f.key
                                  ? <Pin className="w-3.5 h-3.5" strokeWidth={2.4} />
                                  : <PinOff className="w-3.5 h-3.5" strokeWidth={1.75} />}
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
